import { AdFarmConfig } from "./AdFarmConfig";
import { AdFarmApiClient } from "./AdFarmApiClient";
import { AdSource, MobileRole, RewardContext, RewardSessionStatus } from "./AdFarmTypes";
import { RiskService } from "./RiskService";
import { SecureStorage } from "./SecureStorage";

type PendingReward = RewardContext & { rewarded: boolean; createdAt: number };

export class RewardSessionService {
    private static readonly PENDING_KEY = "pending-rewards";

    constructor(private readonly api: AdFarmApiClient, private readonly risk: RiskService) {}

    createTransId(role: MobileRole, taskId: string, actor = "actor"): string {
        const nonce = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        return `${role}-${taskId}-${actor.slice(-4)}-${nonce}`.replace(/[^a-zA-Z0-9._-]/g, "-").slice(0, 120);
    }

    buildRewardExtra(context: RewardContext): string {
        return JSON.stringify({
            appId: AdFarmConfig.APP_ID,
            role: context.role,
            taskId: context.taskId,
            sourceKey: context.sourceKey,
            userId: context.userId,
            transId: context.transId,
            ...(context.placementId ? { placementId: context.placementId } : {}),
        });
    }

    async prepare(input: { role: MobileRole; taskId: string; sourceKey: AdSource; userId: string; placementId?: string }): Promise<RewardContext> {
        await this.risk.checkBeforeAd();
        const transId = this.createTransId(input.role, input.taskId, input.userId);
        const context: RewardContext = { ...input, transId };
        await this.api.createRewardSession({ taskId: input.taskId, sourceKey: input.sourceKey, transId, risk: await this.risk.getPayload() });
        await this.savePending({ ...context, rewarded: false, createdAt: Date.now() });
        return context;
    }

    async report(context: RewardContext, phase: string, name: string, message = ""): Promise<void> {
        try {
            await this.api.reportRewardEvent(context.transId, {
                provider: AdFarmConfig.ADS_PROVIDER,
                phase,
                name,
                message,
                risk: await this.risk.getPayload(),
            });
        } catch (_error) {
            // Diagnostic reporting must not interrupt an ad already in progress.
        }
    }

    async markRewarded(context: RewardContext): Promise<void> {
        const pending = await this.pending();
        const current = pending.find(item => item.transId === context.transId);
        await this.savePending({ ...(current || { ...context, createdAt: Date.now() }), rewarded: true });
    }

    async cancel(context: RewardContext, reason: string): Promise<void> {
        await this.api.cancelRewardSession(context.transId, reason);
        await this.removePending(context.transId);
    }

    async waitForSettlement(context: RewardContext, onStatus?: (status: RewardSessionStatus, attempt: number) => void): Promise<RewardSessionStatus> {
        for (let attempt = 1; attempt <= AdFarmConfig.CALLBACK_ATTEMPTS; attempt++) {
            const status = await this.api.rewardCallbackStatus(context.transId);
            onStatus?.(status, attempt);
            if (status.verified || status.sessionStatus === "settled") {
                await this.removePending(context.transId);
                return status;
            }
            if (["cancelled", "expired", "missing"].indexOf(status.sessionStatus || "") >= 0) {
                await this.removePending(context.transId);
                return status;
            }
            await new Promise<void>(resolve => setTimeout(resolve, AdFarmConfig.CALLBACK_INTERVAL_MS));
        }
        return { verified: false, sessionStatus: "timeout", transId: context.transId };
    }

    async resumeRewarded(onSettled: (context: RewardContext) => Promise<void> | void): Promise<void> {
        const items = (await this.pending()).filter(item => item.rewarded);
        for (const item of items) {
            const status = await this.waitForSettlement(item);
            if (status.verified || status.sessionStatus === "settled") await onSettled(item);
        }
    }

    private async pending(): Promise<PendingReward[]> {
        try {
            return JSON.parse(await SecureStorage.get(RewardSessionService.PENDING_KEY) || "[]") as PendingReward[];
        } catch (_error) {
            return [];
        }
    }

    private async savePending(item: PendingReward): Promise<void> {
        const items = (await this.pending()).filter(existing => existing.transId !== item.transId);
        items.push(item);
        await SecureStorage.set(RewardSessionService.PENDING_KEY, JSON.stringify(items));
    }

    private async removePending(transId: string): Promise<void> {
        const items = (await this.pending()).filter(item => item.transId !== transId);
        await SecureStorage.set(RewardSessionService.PENDING_KEY, JSON.stringify(items));
    }
}
